import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TrackByFunction } from '@angular/core';
import { HxToastrService } from '../../../services/toastr.service';
import {
  AddressModel,
  CityModel,
  ClientFullModel,
  ClientModel,
  ClientPropertyModel,
  CoinTransactionModel,
  CommentModel,
  ContactModel,
  CountryModel,
  HxAuthService,
  HxCityService,
  HxClientService,
  HxCoinService,
  HxCountryService,
  HxUiLabelPipe,
  SeekDirection
} from 'hx-services';
import {
  NgbDropdown,
  NgbDropdownButtonItem,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
  NgbModal,
  NgbNav,
  NgbNavContent,
  NgbNavItem,
  NgbNavItemRole,
  NgbNavLink,
  NgbNavLinkBase,
  NgbNavOutlet,
  NgbTooltip
} from '@ng-bootstrap/ng-bootstrap';
import { HxClientPropertyEditModal } from './client-property-edit-modal/client-property-edit.modal';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgxMaskDirective } from 'ngx-mask';
import { HxDateSelectComponent } from '../../datepicker/date-select/date-select.component';
import { HxBrandSelectComponent } from '../../brand-select/brand-select.component';
import { HxCountrySelectComponent } from '../../country-select/country-select.component';
import { RouterLink } from '@angular/router';
import { HxLoaderComponent } from '../../loader/hx-loader.component';

@Component({
  selector: 'hx-client-edit',
  templateUrl: './client-edit.component.html',
  styleUrls: ['./client-edit.component.css'],
  imports: [NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavContent, NgIf, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgFor, NgbDropdownButtonItem, NgbDropdownItem, FormsModule, NgxMaskDirective, HxDateSelectComponent, NgbTooltip, NgClass, HxBrandSelectComponent, HxCountrySelectComponent, RouterLink, HxLoaderComponent, NgbNavOutlet, DatePipe, TranslocoPipe, HxUiLabelPipe]
})
export class HxClientEditComponent implements OnInit, OnChanges, OnDestroy {
  @Input() clientId!: number;
  @Input() clientName?: string;
  @Input() cityId?: number;
  @Output() clientChanged = new EventEmitter<ClientFullModel>();

  cityMap = new Map<number, CityModel>();
  client?: ClientModel;
  additionalAddresses: AddressModel[] = [];
  additionalContacts: ContactModel[] = [];
  country?: CountryModel;
  countries: CountryModel[] = [];
  activeTab: 'client' | 'brand' = 'client';
  isLoading = {
    clientSubmit: false,
    get: false,
    brandProperties: false,
    coins: false,
    comments: false,
  };
  errorText: { phone?: string } = {};

  customPatterns = {
    'X': {
      pattern: new RegExp('/|0|4|5|6|7|/'),
    }, '9': {
      pattern: new RegExp('/|9|/'),
    }, 'N': {
      pattern: new RegExp('^(?!3)'),
    }, '0': {
      pattern: new RegExp('\\d'),
    }
  };
  brandProperties: ClientPropertyModel[] = [];
  activeCoinAmount = 0;
  coinTransactions: CoinTransactionModel[] = [];
  coinBrandId?: number = 1; //happycake
  coinCountryId?: number = 1; // happycake kz
  seek: {
    last?: string;
    first?: string;
    val?: string;
    hasNext?: boolean;
    dir?: SeekDirection;
  } = {};
  clientBirthDate?: string;
  isBirthDateUpdated = false;
  comment?: string;
  comments: CommentModel[] = [];
  clientLoadFailed = false;
  coinLoadFailed = false;
  hasStoredPhone = false;
  private phoneCheckId = 0;
  private coinRequestId = 0;
  private destroyed = false;

  constructor(
    private clientService: HxClientService,
    private countryService: HxCountryService,
    private cityService: HxCityService,
    private toaster: HxToastrService,
    private coinService: HxCoinService,
    private auth: HxAuthService,
    private modal: NgbModal,
    private tr: TranslocoService
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientName'] && this.client) {
      this.client.fullname = this.clientName ?? '';
    }
  }

  ngOnInit() {
    void this.loadClient();
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.coinRequestId++;
  }

  async loadClient() {
    this.isLoading.get = true;
    this.clientLoadFailed = false;
    try {
      this.countries = await this.countryService.getCountries();
      if (this.clientId) {
        const response = await this.clientService.getClient(this.clientId);
        if (!this.destroyed) {
          this.initClient(response);
        }
      } else if (this.cityId) {
        const country = await this.countryService.getCountryByCityId(this.cityId);
        if (country) {
          this.coinCountryId = country.id;
          this.selectCountry(country);
        }
      } else {
        this.additionalContacts = [];
        this.additionalAddresses = [];
      }
    } catch (error) {
      if (!this.destroyed) {
        this.clientLoadFailed = true;
        this.toaster.error(this.tr.translate('hx.client-list.loadError'));
      }
    } finally {
      if (!this.destroyed) {
        this.isLoading.get = false;
      }
    }
  }

  selectCountry(country: CountryModel) {
    this.country = country;

    if (country) {
      if (this.client) {
        this.client.countryId = country.id;
      }
      this.cityService.getAllCities({countryId: country.id})
        .then(cities => cities.forEach(city => this.cityMap.set(city.id, city)))
        .catch(() => this.toaster.error(this.tr.translate('hx.client-list.loadError')));
    }
  }

  addAContact(type: string) {
    if (!this.client) {
      return;
    }
    this.additionalContacts.push({type: type} as ContactModel);
  }

  removeContact(index: number) {
    if (!this.client) {
      return;
    }
    this.additionalContacts.splice(index, 1);
  }

  checkPhoneExist() {
    if (!this.client) {
      return;
    }
    const checkId = ++this.phoneCheckId;
    this.errorText.phone = undefined;
    if (this.hasStoredPhone) {
      return;
    }
    let phone = this.client.phone ?? '';
    const country = this.country;
    if (phone && country) {
      phone = phone.replace(/\D+/g, '');
      if (phone.length === country.phoneLength) {
        this.clientService.getClientByPhone(country.phonePrefix.replace(/\D+/g, '') + phone).then(result => {
          if (checkId === this.phoneCheckId && result && result.client.id !== this.client?.id) {
            this.errorText.phone = 'client.exist.byphone';
          }
        }, err => {
          if (checkId === this.phoneCheckId && err.error?.message !== 'client.notFound') {
            this.errorText.phone = 'unexpected';
          }
        });
      }
    }
  }

  async submit() {
    if (!this.client || this.isLoading.clientSubmit) {
      return;
    }
    if (this.clientBirthDate && this.isBirthDateUpdated && !this.comment?.trim()) {
      this.toaster.error(this.tr.translate('hx.client-edit.ts.commentEmpty'));
      return;
    }
    this.isLoading.clientSubmit = true;
    const client = this.clientService.buildRequest({ client: this.client, additionalContacts: this.additionalContacts, additionalAddresses: this.additionalAddresses });
    client.phone = this.rebuildPhone(client.phone);
    client.comment = this.comment;

    client.isBirthDateUpdated = this.isBirthDateUpdated;
    (client.additionalContacts ?? [])
      .filter(contact => contact.type === 'PHONE')
      .forEach(contact => contact.value = this.rebuildPhone(contact.value));

    try {
      const result = await this.clientService.saveClient(client);
      const savedClient = await this.clientService.getClient(result.id);
      this.clientChanged.emit(savedClient);
      this.initClient(savedClient);
      this.isBirthDateUpdated = false;
      this.toaster.success(this.tr.translate('hx.client-edit.ts.dataUpdated'));
    } finally {
      this.isLoading.clientSubmit = false;
    }
  }

  cancel(): void {
    this.clientChanged.emit();
  }

  trackById: TrackByFunction<any> = (index: number, obj: { id: number }) => obj.id;

  removeAddress(addr: AddressModel) {
    if (this.client && this.additionalAddresses) {
      this.additionalAddresses = this.additionalAddresses.filter(el => el.id !== addr.id);
    }
  }

  onTabChanged(tab: string) {
    console.log('onTabChanged', tab);
    if (tab === 'brand') {
      this.loadProperties();
    } else if (tab === 'coins') {
      this.loadCoins();
    } else if (tab === 'comments') {
      this.loadComments();
    }
  }

  async editBrandProperty(item: ClientPropertyModel) {
    const modalInstance = this.modal.open(HxClientPropertyEditModal, {size: 'lg'});
    modalInstance.componentInstance.client = this.client;
    modalInstance.componentInstance.property = item;
    await modalInstance.result;
    this.loadProperties();
  }

  async addBrandProperty() {
    const modalInstance = this.modal.open(HxClientPropertyEditModal, {size: 'lg'});
    modalInstance.componentInstance.client = this.client;
    await modalInstance.result;
    this.loadProperties();
  }

  removeBrandProperty(item: ClientPropertyModel) {
    this.clientService.removeClientBrandProperty(item.id).then(() => this.loadProperties());
  }

  seekPrev() {
    void this.loadCoinTransactions({seek: this.seek.first, seekDir: SeekDirection.prev});
  }

  seekNext() {
    void this.loadCoinTransactions({seek: this.seek.last, seekDir: SeekDirection.next});
  }

  onCoinFilterChanged() {
    this.loadCoins();
  }

  async loadCoins() {
    if (!this.coinCountryId || !this.coinBrandId) {
      this.coinRequestId++;
      this.coinTransactions = [];
      this.activeCoinAmount = 0;
      this.isLoading.coins = false;
      return;
    }
    const requestId = ++this.coinRequestId;
    try {
      this.isLoading.coins = true;
      this.coinLoadFailed = false;
      this.coinTransactions = [];
      const countryId = this.coinCountryId;
      const brandId = this.coinBrandId;
      const amount = await this.coinService.getActiveCoins({clientId: this.clientId, countryId, brandId});
      const result = await this.coinService.getCoinTransaction({clientId: this.clientId, countryId, brandId, limit: 20});
      if (requestId === this.coinRequestId && !this.destroyed) {
        this.activeCoinAmount = amount;
        this.setCoinTransactions(result, {});
      }
    } catch (err) {
      if (requestId === this.coinRequestId && !this.destroyed) {
        this.coinLoadFailed = true;
        this.toaster.error('unexpected');
      }
    } finally {
      if (requestId === this.coinRequestId && !this.destroyed) {
        this.isLoading.coins = false;
      }
    }
  }

  async loadComments() {
    try {
      this.isLoading.comments = true;
      this.comments = await this.clientService.getClientComments(this.clientId);
    } finally {
      this.isLoading.comments = false;
    }
  }

  onDateChanged() {
    this.isBirthDateUpdated = false;
    if (this.clientBirthDate !== this.client?.birthDate) {
      this.isBirthDateUpdated = true;
    } else {
      this.comment = undefined;
    }
  }

  transformPhone(phone?: string): string {
    if (!phone) {
      return '';
    }
    if (!this.client || !this.country) {
      return phone;
    }
    return phone.substring(phone.length - this.country.phoneLength);
  }

  private async loadProperties() {
    this.isLoading.brandProperties = true;
    try {
      this.brandProperties = await this.clientService.getClientBrandProperties(this.clientId);
    } finally {
      this.isLoading.brandProperties = false;
    }
  }

  private rebuildPhone(phone: string) {
    if (!this.client) {
      return phone;
    }
    return (this.country?.phonePrefix + phone).replace(/\D+/g, '');
  }

  private initClient(data: ClientFullModel) {
    this.hasStoredPhone = !!data.client.phone;
    this.client = data.client;
    this.country = data.country;
    this.additionalAddresses = data.additionalAddresses ?? [];
    this.additionalContacts = data.additionalContacts ?? [];

    this.client.phone = this.transformPhone(this.client.phone);
    this.additionalContacts.filter(c => c.type === 'PHONE' && c.value && c.value !== data.client.phone).forEach(contact => {
      contact.value = this.transformPhone(contact.value);
    });

    if (this.country) {
      this.coinCountryId = this.country.id;
      this.selectCountry(this.country);
    }
    if (this.clientName) {
      this.client.fullname = this.clientName;
    }
    this.clientBirthDate = this.client.birthDate;
    this.loadProperties();
  }

  private async loadCoinTransactions(params?: {seek?: string, seekDir?: SeekDirection}) {
    if (!this.coinCountryId || !this.coinBrandId) {
      return;
    }
	const requestId = ++this.coinRequestId;
	this.isLoading.coins = true;
	this.coinLoadFailed = false;
	try {
		const result = await this.coinService.getCoinTransaction({
			clientId: this.clientId,
			countryId: this.coinCountryId,
			brandId: this.coinBrandId,
			limit: 20,
			seek: params?.seek,
			seekDirection: params?.seekDir,
		});
		if (requestId === this.coinRequestId && !this.destroyed) {
			this.setCoinTransactions(result, params);
		}
	} catch (error) {
		if (requestId === this.coinRequestId && !this.destroyed) {
			this.coinLoadFailed = true;
			this.toaster.error('unexpected');
		}
	} finally {
		if (requestId === this.coinRequestId && !this.destroyed) {
			this.isLoading.coins = false;
		}
	}
  }

	private setCoinTransactions(result: {list: CoinTransactionModel[], first?: string, last?: string, hasNext?: boolean}, params?: {seek?: string, seekDir?: SeekDirection}) {
		this.seek.dir = params?.seekDir;
		this.seek.val = params?.seek;
		this.seek.first = result.first;
		this.seek.last = result.last;
		this.seek.hasNext = result.hasNext;
		this.coinTransactions = result.list;
	}
}
